import React, { useState, useEffect, useRef } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";

import { FormHelper } from "utils";
import { Perfil } from "models/Perfil";
import { Usuario } from "models/usuario";
import useMessage from "hooks/useMessage";
import { GridView } from "components";
import useExceptionHandler from "hooks/useExceptionHandler";
import usePaginationValuesWhere from "hooks/usePaginationValuesWhere";
import SectionRow from "components/PantallasEdit/SectionRow/SectionRow";
import { PerfilManager, UsuarioManager } from "managers";
import EditSection from "components/PantallasEdit/EditSection/EditSection";
import InputContainer from "components/PantallasEdit/InputContainer/InputContainer";
import CambiarContraseniaModal from "components/Model/CambiarContraseniaModal";
import { useAuthentication } from "hooks";
import { Permisos } from "utils/permisos";
import { Simplebar } from "components/Simplebar/Simplebar";

export default function UsuarioEdit({
  showLoading,
  hideLoading,
  IdSec,
  esClonar = false,
  showCanvas,
  setShowCanvas,
  updateList = () => {},
}) {
  const [tituloHeader, setTituloHeader] = useState("Crear Usuario");
  const [usuario, setUsuario] = useState({});
  const { Message, cerrarAlertas } = useMessage();
  const { handleException } = useExceptionHandler();
  const [esNuevo, setEsNuevo] = useState(true);
  const [perfilesList, setPerfilesList] = useState([]);
  const [perfilesGrid, setPerfilesGrid] = useState([]);
  const [mostrarModalCambiarContrasenia, setMostrarModalCambiarContrasenia] =
    useState(false);
  const { tienePermiso } = useAuthentication();
  const { setPaginationValuesWhere } = usePaginationValuesWhere();
  const [where, setWhere] = useState({});
  const [pagination, setPagination] = useState({ EsPaginable: false });
  const ref = useRef(null);
  const ESTATUS_LIST = Usuario.ESTATUS_LIST;

  useEffect(() => {
    if (!showCanvas) return;
    setEsNuevo(!IdSec || esClonar);
    if (IdSec) {
      showLoading();
      cargarUsuario();
      esClonar
        ? setTituloHeader("Clonar Usuario")
        : setTituloHeader("Editar Usuario");
    } else {
      setTituloHeader("Crear Usuario");
      setUsuario({});
      setPerfilesList([]);
      setPerfilesGrid([]);
    }
    return () => {
      hideLoading();
      cerrarAlertas();
    };
  }, [IdSec, showCanvas]);

  useEffect(() => {
    setPerfilesGrid(filterList());
  }, [where]);

  const grabar = () => {
    showLoading();
    if (esNuevo) {
      UsuarioManager.crear(usuario)
        .then((res) => {
          setEsNuevo(false);
          Message({ message: `Grabado Exitosamente` });
          cargarUsuario(res.IdSec);
          updateList();
        })
        .catch((e) => handleException(e))
        .finally(() => hideLoading());
    } else {
      UsuarioManager.actualizar(usuario)
        .then(() => {
          Message({ message: `Actualizado Exitosamente` });
          updateList();
        })
        .catch((e) => handleException(e))
        .finally(() => hideLoading());
    }
  };

  const cargarUsuario = (IdSecParam) => {
    UsuarioManager.buscarPorId(IdSecParam ? IdSecParam : IdSec)
      .then((data) => {
        setUsuario(data);
        cargarPerfilesList(data.PerfilesUsuario);
      })
      .catch((e) => handleException(e))
      .finally(() => hideLoading());
  };

  function filterList() {
    let list = [...perfilesList];
    return list.filter((el) => {
      return (
        (where.IdSec
          ? el.IdSec.toString().includes(where.IdSec.toString())
          : true) &&
        (where.Nombre
          ? el.Nombre?.toLowerCase().includes(where.Nombre?.toLowerCase())
          : true) &&
        (where.Descripcion
          ? el.Descripcion?.toLowerCase().includes(
              where.Descripcion?.toLowerCase()
            )
          : true)
      );
    });
  }

  function cargarPerfilesList(perfilesUsuario) {
    PerfilManager.listado({
      Estatus: Perfil.DEFAULT_ESTATUS_VALUE,
      EsPaginable: false,
    })
      .then((res) => {
        setPaginationValuesWhere(res.Headers, pagination, setPagination);
        let list = res.Data;
        perfilesUsuario.map((el) => {
          let index = list.findIndex((perfil) => perfil.IdSec === el.IdSec);
          if (index !== -1) {
            list[index].EsAnadirUsuario = true;
          } else {
            list[index].EsAnadirUsuario = false;
          }
        });
        sortPerfilesList(list);
      })
      .catch((e) => handleException(e))
      .finally(() => hideLoading());
  }

  function sortPerfilesList(list) {
    if (!list) {
      list = [...perfilesList];
    }
    list.sort((a, b) => {
      return Number(b.EsAnadirUsuario) - Number(a.EsAnadirUsuario);
    });
    setPerfilesList(list);
    setPerfilesGrid(list);
  }

  function addToListClick(Id) {
    const index = perfilesList.findIndex((perfil) => perfil.IdSec === Id);
    usuario.PerfilesUsuario.push(perfilesList[index]);
  }

  function removeFromList(Id) {
    const index = usuario.PerfilesUsuario.findIndex(
      (perfil) => perfil.IdSec === Id
    );
    setPerfilesList([...perfilesList, usuario.PerfilesUsuario[index]]);
    usuario.PerfilesUsuario.splice(index, 1);
  }

  const handlePerfilSwitch = ({ Id }) => {
    const index = perfilesList.findIndex((perfil) => perfil.IdSec === Id);
    let list = [...perfilesList];
    if (list[index].EsAnadirUsuario) {
      removeFromList(Id);
    } else {
      addToListClick(Id);
    }
    list[index].EsAnadirUsuario = !perfilesList[index].EsAnadirUsuario;
    setPerfilesList(list);
  };

  const PerfilesGrid = (
    <GridView
      pagination={pagination}
      setPagination={setPagination}
      allowPaging={pagination.EsPaginable}
      pageSize={pagination.CantidadPorPagina}
      pageIndex={pagination.IndiceDePagina}
      lastPageNumber={pagination.PaginasTotal}
      dataSource={perfilesGrid}
      where={where}
      setWhere={setWhere}
      height="30rem"
    >
      <GridView.Column
        headerText="Id"
        dataField="IdSec"
        sortExpression="IdSec"
      />
      <GridView.Column
        headerText="Nombre"
        dataField="Nombre"
        sortExpression="Nombre"
      />
      <GridView.Column
        headerText="Descripción"
        dataField="Descripcion"
        sortExpression="Descripcion"
      />
      <GridView.Column
        isField
        bodyRenderer={(row) => (
          <div className="float-right mr-4">
            <Form.Check
              type="switch"
              name="AgregarUsuario"
              onChange={() => handlePerfilSwitch({ Id: row.IdSec })}
              checked={row.EsAnadirUsuario}
              id={`switch-${row.IdSec}`}
            />
          </div>
        )}
      />
    </GridView>
  );

  return (
    <>
      {mostrarModalCambiarContrasenia && (
        <CambiarContraseniaModal
          setMostrarModalCambiarContrasenia={setMostrarModalCambiarContrasenia}
          mostrarModalCambiarContrasenia={mostrarModalCambiarContrasenia}
          user={usuario}
          noPedirContraseniaActual={true}
        />
      )}
      <Simplebar
        ref={ref}
        backdrop="static"
        confirmBack
        placement="end"
        size="xxl"
        portal={false}
        visible={showCanvas}
        setShow={(e) => setShowCanvas(e)}
        useLoading={true}
        title={tituloHeader}
        btnLeft={{
          show: true,
          message: "Grabar",
          isComponent: true,
          size: "45px",
          height: "24px",
          with: "24px",
          iconName: "save",
          isCenter: true,
          func: grabar,
        }}
        btnRight={{
          show: tienePermiso(Permisos.Usuario.ACTUALIZAR_PASSWORD) && !esNuevo,
          message: "Cambiar Contraseña",
          isComponent: true,
          size: "45px",
          height: "24px",
          with: "24px",
          iconName: "changepassword",
          isCenter: true,
          func: () => setMostrarModalCambiarContrasenia(true),
        }}
      >
        <Form>
          <SectionRow>
            <InputContainer label="Nombre">
              <Form.Control
                onChange={(e) => FormHelper.handleChange(e, setUsuario)}
                name="Descripcion"
                type="text"
                placeholder="Nombre Completo"
                value={usuario.Descripcion}
              />
            </InputContainer>
            <InputContainer label="Usuario">
              <Form.Control
                name="NombreUsuario"
                type="text"
                onChange={(e) => FormHelper.handleChange(e, setUsuario)}
                value={usuario.NombreUsuario}
                placeholder="Nombre de Usuario"
              />
            </InputContainer>
          </SectionRow>
          <SectionRow>
            <InputContainer label="Correo">
              <Form.Control
                onChange={(e) => FormHelper.handleChange(e, setUsuario)}
                name="Email"
                type="email"
                placeholder="Correo Electrónico"
                value={usuario.Email}
              />
            </InputContainer>
            <InputContainer label="Estatus">
              <Select
                isDisabled={esNuevo}
                className="react-select primary"
                classNamePrefix="react-select"
                name="Estatus"
                onChange={(e) => setUsuario({ ...usuario, Estatus: e.value })}
                options={ESTATUS_LIST}
                value={
                  esNuevo
                    ? ESTATUS_LIST.filter(
                        (o) => o.value === Usuario.DEFAULT_ESTATUS_VALUE
                      )
                    : ESTATUS_LIST.filter((o) => o.value === usuario.Estatus)
                }
              />
            </InputContainer>
          </SectionRow>
          {esNuevo && (
            <EditSection title="Seguridad">
              <SectionRow>
                <InputContainer label="Contraseña">
                  <Form.Control
                    name="nuevoPassword"
                    type="password"
                    onChange={(e) => FormHelper.handleChange(e, setUsuario)}
                    value={usuario.nuevoPassword}
                  />
                </InputContainer>
                <InputContainer label="Confirmar">
                  <Form.Control
                    name="passwordConfirm"
                    type="password"
                    onChange={(e) => FormHelper.handleChange(e, setUsuario)}
                    value={usuario.passwordConfirm}
                    placeholder="Confirmar Contraseña"
                  />
                </InputContainer>
              </SectionRow>
            </EditSection>
          )}
          {!esNuevo && (
            <EditSection title="Perfiles">{PerfilesGrid}</EditSection>
          )}
        </Form>
      </Simplebar>
    </>
  );
}
